// The Technology hand-off body, rendered in the two places a customer can meet
// it: the /technology-request route (../pages/TechnologyRequest.tsx), where the
// Technology entry door lands, and inline on a quote item whose product is
// "Something else" (mounted by ../pages/QuoteEdit.tsx). Both say the same thing
// — this is handled by a person, not by the order form, and here is who to reach
// — so a second copy would drift from the first the day the support number or
// the list of covered requests changes.
//
// No wrapping element of its own, and deliberately: the route frames it as a
// `card request-stub` reading column, while the item mounts it in the flow of an
// already-bordered configurator column, where a card would be a third stacked
// surface. Each host supplies the frame its own context wants.
//
// One `context` prop, not a row of booleans, because the differences are not
// independent — they are all the same difference. The route is a dead end with
// no form on it, so it needs the scope statement (which requests our team
// handles directly rather than taking as an order), it asks for the request in
// prose, and it needs a way out. The item has already asked: the scope statement
// is the "?" definition of `something_else_request` ("Tell us what you need"),
// authored in the seed, and the customer typed their answer into the box above —
// so printing either here is the wider surface restating the narrower one
// (CLAUDE.md R36), and a back link would point them away from their own work.
// What is left for the item is the one fact the fields do not carry: a person
// picks this up, and here is who.

import { Trans, useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { SUPPORT_EMAIL, SUPPORT_PHONE } from '../pricingCopy.js';

export function TechnologyHandoff({
  context = 'route',
  headingLevel = 'h2',
}: {
  context?: 'route' | 'item';
  headingLevel?: 'h2' | 'h3';
}): JSX.Element {
  const { t } = useTranslation(['quote', 'common']);
  const Heading = headingLevel;
  const onRoute = context === 'route';

  return (
    <>
      <Heading>{t('technologyRequest.title')}</Heading>
      {onRoute && <p>{t('technologyRequest.intro')}</p>}
      <p>{onRoute ? t('technologyRequest.nextStep') : t('technologyRequest.followUp')}</p>
      <p>
        <Trans
          i18nKey="technologyRequest.contact"
          t={t}
          values={{ email: SUPPORT_EMAIL, phone: SUPPORT_PHONE }}
          components={[
            <a key="email" href={`mailto:${SUPPORT_EMAIL}`} />,
            <a key="phone" href={`tel:${SUPPORT_PHONE.replace(/[^0-9+]/g, '')}`} />,
          ]}
        />
      </p>
      {onRoute && (
        <div className="actions" style={{ marginTop: 'var(--space-4)' }}>
          <Link className="link-button" to="/quotes/new">
            {t('technologyRequest.back')}
          </Link>
        </div>
      )}
    </>
  );
}
